<script setup>
import { ref, watch, computed } from 'vue'
import { requiredValidator } from '../../../utils/validators'
import { useCmsContentStore } from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'

const props = defineProps({
  contentItem: {
    type: Object,
    default: null,
  },
  isDialogVisible: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:isDialogVisible', 'saved'])

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth(750)

const cmsStore = useCmsContentStore()
const form = ref()
const saving = ref(false)
const changeNotes = ref('')
const showVersionHistory = ref(false)
const versions = ref([])
const loadingVersions = ref(false)

// Local editable copy of the item
const editItem = ref({})

const categories = [
  { title: 'Marketing', value: 'marketing' },
  { title: 'Navigation', value: 'navigation' },
  { title: 'Form', value: 'form' },
  { title: 'Action', value: 'action' },
  { title: 'Validation', value: 'validation' },
  { title: 'Filter', value: 'filter' },
  { title: 'Dialog', value: 'dialog' },
  { title: 'Empty State', value: 'empty-state' },
  { title: 'Help', value: 'help' },
  { title: 'Testimonial', value: 'testimonial' },
]

const contentTypes = [
  { title: 'Text', value: 'text' },
  { title: 'Rich Text', value: 'richtext' },
  { title: 'Block (Structured)', value: 'block' },
]

// Tiptap config for richtext editing
const tiptapOutput = ref('html')
const tiptapMarkdownTheme = ref('')
const tiptapOutlined = ref(true)
const tiptapMaxWidth = ref(900)

// When contentItem prop changes, populate local copy
watch(() => props.contentItem, newVal => {
  if (newVal) {
    editItem.value = JSON.parse(JSON.stringify(newVal))
    changeNotes.value = ''
    showVersionHistory.value = false
    versions.value = []
  }
}, { immediate: true })

const isBlock = computed(() => editItem.value?.contentType === 'block')
const isRichText = computed(() => editItem.value?.contentType === 'richtext')
const isText = computed(() => !editItem.value?.contentType || editItem.value?.contentType === 'text')

const updateModelValue = val => {
  emit('update:isDialogVisible', val)
}

const onFormSubmit = async () => {
  const { valid: isValid } = await form.value.validate()
  if (!isValid) return

  try {
    saving.value = true
    await cmsStore.updateContentItem(editItem.value._id, {
      value: editItem.value.value,
      displayName: editItem.value.displayName,
      description: editItem.value.description,
      category: editItem.value.category,
      contentType: editItem.value.contentType,
      blockSchema: editItem.value.blockSchema,
      changeNotes: changeNotes.value,
    })
    emit('saved')
    emit('update:isDialogVisible', false)
  } catch (error) {
    console.error('Error saving CMS content:', error)
  } finally {
    saving.value = false
  }
}

const loadVersionHistory = async () => {
  if (!editItem.value?._id) return
  try {
    loadingVersions.value = true
    versions.value = await cmsStore.getVersions(editItem.value._id)
  } finally {
    loadingVersions.value = false
  }
}

const toggleVersionHistory = async () => {
  showVersionHistory.value = !showVersionHistory.value
  if (showVersionHistory.value && versions.value.length === 0) {
    await loadVersionHistory()
  }
}

const rollback = async version => {
  try {
    saving.value = true
    await cmsStore.rollbackToVersion(editItem.value._id, version)
    emit('saved')
    emit('update:isDialogVisible', false)
  } catch (error) {
    console.error('Error rolling back:', error)
  } finally {
    saving.value = false
  }
}

// Block editor helpers
const addBlockItem = () => {
  if (!Array.isArray(editItem.value.value)) {
    editItem.value.value = []
  }
  const newItem = {}
  if (Array.isArray(editItem.value.blockSchema)) {
    for (const field of editItem.value.blockSchema) {
      newItem[field.field] = ''
    }
  }
  editItem.value.value.push(newItem)
}

const removeBlockItem = index => {
  editItem.value.value.splice(index, 1)
}

const moveBlockItem = (index, direction) => {
  const arr = editItem.value.value
  const newIndex = index + direction
  if (newIndex < 0 || newIndex >= arr.length) return
  const temp = arr[index]
  arr[index] = arr[newIndex]
  arr[newIndex] = temp
}

const formatDate = dateStr => {
  if (!dateStr) return ''
  
  return new Date(dateStr).toLocaleString()
}
</script>

<template>
  <VDialog
    :model-value="isDialogVisible"
    :width="dWidth"
    @update:model-value="updateModelValue"
  >
    <VCard
      v-if="editItem"
      class="pa-sm-9 pa-5 pt-4"
    >
      <VCardItem class="text-center">
        <VCardTitle class="text-h5">
          Edit Content
        </VCardTitle>
        <VCardSubtitle class="mt-4">
          <code>{{ editItem.key }}</code>
        </VCardSubtitle>
      </VCardItem>

      <VCardText>
        <VForm
          ref="form"
          class="mt-4"
          @submit.prevent="onFormSubmit"
        >
          <VRow>
            <!-- Display Name -->
            <VCol cols="12">
              <VTextField
                v-model="editItem.displayName"
                label="Display Name"
                hint="Human-readable name for this content item"
                persistent-hint
              />
            </VCol>

            <!-- Description -->
            <VCol cols="12">
              <VTextField
                v-model="editItem.description"
                label="Description"
                hint="Where/how this content is used"
                persistent-hint
              />
            </VCol>

            <!-- Category -->
            <VCol
              cols="12"
              sm="6"
            >
              <VSelect
                v-model="editItem.category"
                label="Category"
                :items="categories"
                :rules="[requiredValidator]"
              />
            </VCol>

            <!-- Content Type -->
            <VCol
              cols="12"
              sm="6"
            >
              <VSelect
                v-model="editItem.contentType"
                label="Content Type"
                :items="contentTypes"
              />
            </VCol>

            <!-- Text Value -->
            <VCol
              v-if="isText"
              cols="12"
            >
              <VTextarea
                v-model="editItem.value"
                label="Value"
                :rules="[requiredValidator]"
                rows="3"
                auto-grow
              />
            </VCol>

            <!-- Rich Text Value -->
            <VCol
              v-if="isRichText"
              cols="12"
            >
              <div class="text-body-2 mb-1">
                Value (Rich Text)
              </div>
              <VuetifyTiptap
                v-model="editItem.value"
                v-model:markdown-theme="tiptapMarkdownTheme"
                label="Content"
                :output="tiptapOutput"
                :outlined="tiptapOutlined"
                rounded
                :max-height="400"
                :max-width="tiptapMaxWidth"
              />
            </VCol>

            <!-- Block Value -->
            <VCol
              v-if="isBlock"
              cols="12"
            >
              <div class="d-flex align-center mb-3">
                <div class="text-body-2 font-weight-medium">
                  Block Items ({{ Array.isArray(editItem.value) ? editItem.value.length : 0 }})
                </div>
                <VSpacer />
                <VBtn
                  size="small"
                  variant="outlined"
                  @click="addBlockItem"
                >
                  Add Item
                </VBtn>
              </div>

              <template v-if="Array.isArray(editItem.value)">
                <VCard
                  v-for="(blockItem, blockIndex) in editItem.value"
                  :key="blockIndex"
                  class="mb-3 pa-4"
                  variant="outlined"
                >
                  <div class="d-flex align-center mb-2">
                    <span class="text-caption font-weight-medium">Item {{ blockIndex + 1 }}</span>
                    <VSpacer />
                    <VBtn
                      icon="mdi-arrow-up"
                      size="x-small"
                      variant="text"
                      :disabled="blockIndex === 0"
                      @click="moveBlockItem(blockIndex, -1)"
                    />
                    <VBtn
                      icon="mdi-arrow-down"
                      size="x-small"
                      variant="text"
                      :disabled="blockIndex === editItem.value.length - 1"
                      @click="moveBlockItem(blockIndex, 1)"
                    />
                    <VBtn
                      icon="mdi-delete-outline"
                      size="x-small"
                      variant="text"
                      color="error"
                      @click="removeBlockItem(blockIndex)"
                    />
                  </div>

                  <template v-if="Array.isArray(editItem.blockSchema)">
                    <template
                      v-for="fieldDef in editItem.blockSchema"
                      :key="fieldDef.field"
                    >
                      <VTextarea
                        v-if="fieldDef.type === 'textarea'"
                        v-model="blockItem[fieldDef.field]"
                        :label="fieldDef.label"
                        :rules="fieldDef.required ? [requiredValidator] : []"
                        rows="2"
                        auto-grow
                        class="mb-2"
                      />
                      <VTextField
                        v-else
                        v-model="blockItem[fieldDef.field]"
                        :label="fieldDef.label"
                        :rules="fieldDef.required ? [requiredValidator] : []"
                        class="mb-2"
                      />
                    </template>
                  </template>

                  <!-- Fallback: raw JSON fields if no blockSchema -->
                  <template v-else>
                    <VTextField
                      v-for="(val, fieldKey) in blockItem"
                      :key="fieldKey"
                      v-model="blockItem[fieldKey]"
                      :label="fieldKey"
                      class="mb-2"
                    />
                  </template>
                </VCard>
              </template>
            </VCol>

            <!-- Change Notes -->
            <VCol cols="12">
              <VTextField
                v-model="changeNotes"
                label="Change Notes (optional)"
                hint="Describe what changed"
                persistent-hint
              />
            </VCol>

            <!-- Version History Toggle -->
            <VCol cols="12">
              <VBtn
                variant="text"
                size="small"
                :loading="loadingVersions"
                @click="toggleVersionHistory"
              >
                {{ showVersionHistory ? 'Hide' : 'Show' }} Version History (v{{ editItem.version }})
              </VBtn>

              <div v-if="showVersionHistory">
                <VList
                  v-if="versions.length > 0"
                  density="compact"
                  class="mt-2"
                >
                  <VListItem
                    v-for="v in versions"
                    :key="v._id"
                    class="mb-1"
                  >
                    <template #prepend>
                      <VChip
                        size="x-small"
                        class="mr-2"
                      >
                        v{{ v.version }}
                      </VChip>
                    </template>
                    <VListItemTitle class="text-caption">
                      {{ v.changeNotes || 'No notes' }}
                    </VListItemTitle>
                    <VListItemSubtitle class="text-caption">
                      {{ formatDate(v.createdAt) }}
                      <span v-if="v.modifiedBy">
                        by {{ v.modifiedBy.firstName }} {{ v.modifiedBy.lastName }}
                      </span>
                    </VListItemSubtitle>
                    <template #append>
                      <VBtn
                        size="x-small"
                        variant="text"
                        color="warning"
                        @click="rollback(v.version)"
                      >
                        Rollback
                      </VBtn>
                    </template>
                  </VListItem>
                </VList>
                <div
                  v-else
                  class="text-caption text-disabled mt-2"
                >
                  No version history yet
                </div>
              </div>
            </VCol>

            <!-- Submit and Cancel -->
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                :loading="saving"
                @click="onFormSubmit"
              >
                Save
              </VBtn>
              <VBtn
                color="secondary"
                variant="tonal"
                @click="emit('update:isDialogVisible', false)"
              >
                Cancel
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>
